import { useCallback, useEffect, useMemo, useState } from "react";
import type { AvatarConfig } from "./types";

const LOCAL_STORAGE_KEY = "retroboard-avatar-config";
const EVENT_NAME = "retroboard-avatar-config-changed";

const DEFAULT_CONFIG: AvatarConfig = { height: "medium", hatIndex: null, weapon: null, hasShield: false };

function loadConfig(): AvatarConfig {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return DEFAULT_CONFIG;
}

function saveConfig(config: AvatarConfig) {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(config));
  queueMicrotask(() => {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: config }));
  });
}

type ConfigUpdater = AvatarConfig | ((prev: AvatarConfig) => AvatarConfig);

export function useAvatarConfig(): [AvatarConfig, (updater: ConfigUpdater) => void] {
  const [config, setConfigState] = useState<AvatarConfig>(loadConfig);

  const setConfig = useCallback((updater: ConfigUpdater) => {
    setConfigState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      saveConfig(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      setConfigState((e as CustomEvent<AvatarConfig>).detail);
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  return [config, setConfig];
}

export function useAvatarConfigWithGearFlag(gearEnabled: boolean): [AvatarConfig, (updater: ConfigUpdater) => void] {
  const [config, setConfig] = useAvatarConfig();

  const effectiveConfig = useMemo<AvatarConfig>(() => {
    if (gearEnabled) return config;
    return { height: config.height, hatIndex: null, weapon: null, hasShield: false };
  }, [config, gearEnabled]);

  return [effectiveConfig, setConfig];
}
